// Bloques dinámicos de la home. Cada bloque es de uno de 5 tipos:
//   - featuredHero / cards / featuredCollection → apuntan a una colección
//   - artists / cities → bloques fijos (solo posición y activo/inactivo)
//
// El orden en el array `blocks` define el orden vertical en la home.
// Los bloques artists/cities tienen contenido propio gestionado desde
// sus respectivas páginas del admin (artistas-destacados y ciudades-destacadas);
// aquí solo se controla su posición y si están activos.
import { z } from 'zod';

const CollectionBlockTypeSchema = z.enum(['featuredHero', 'cards', 'featuredCollection']);
const StaticBlockTypeSchema = z.enum(['artists', 'cities']);

export const HomeBlockTypeSchema = z.enum([
  'featuredHero', 'cards', 'featuredCollection', 'artists', 'cities',
]);
export type HomeBlockType = z.infer<typeof HomeBlockTypeSchema>;

function collectionBlockSchema(type: z.infer<typeof CollectionBlockTypeSchema>) {
  return z.object({
    id: z.string().min(1),
    type: z.literal(type),
    collectionId: z.string().min(1),
    isActive: z.boolean(),
  });
}

function staticBlockSchema(type: z.infer<typeof StaticBlockTypeSchema>) {
  return z.object({
    id: z.string().min(1),
    type: z.literal(type),
    isActive: z.boolean(),
  });
}

export const HomeBlockSchema = z.discriminatedUnion('type', [
  collectionBlockSchema('featuredHero'),
  collectionBlockSchema('cards'),
  collectionBlockSchema('featuredCollection'),
  staticBlockSchema('artists'),
  staticBlockSchema('cities'),
]);
export type HomeBlock = z.infer<typeof HomeBlockSchema>;

// Helper para diferenciar en service/UI sin repetir el `in`
export function isCollectionBlockType(type: HomeBlockType): boolean {
  return type === 'featuredHero' || type === 'cards' || type === 'featuredCollection';
}

// Type predicate sobre el bloque completo (no solo el `type`): permite
// que TypeScript estreche a la variante con `collectionId` sin castear
// a mano en cada call site (api/admin).
export function isCollectionBlock(
  block: HomeBlock,
): block is Extract<HomeBlock, { collectionId: string }> {
  return isCollectionBlockType(block.type);
}

// artists/cities son bloques fijos: siempre presentes en la home (el
// admin solo controla su posición e isActive, nunca los añade ni los
// quita). Se exige exactamente uno de cada uno en cada guardado para
// que la invariante se garantice en el límite de validación, no solo
// en que la UI del admin nunca ofrezca quitarlos.
function exactlyOneOfType(blocks: HomeBlock[], type: 'artists' | 'cities'): boolean {
  return blocks.filter((b) => b.type === type).length === 1;
}

// Tope de bloques por guardado. La home no tiene ningún caso de uso
// cerca de este número; el límite existe para que el documento no
// pueda crecer sin control (cada bloque de colección se traduce en
// una lectura a Cosmos al servir la home pública).
export const MAX_HOME_BLOCKS = 20;

// Los `id` identifican al bloque en el drag&drop del admin y como key
// de React; duplicarlos rompe las dos cosas. La UI genera ids únicos,
// pero la garantía tiene que vivir en el límite de validación.
function hasUniqueIds(blocks: HomeBlock[]): boolean {
  return new Set(blocks.map((b) => b.id)).size === blocks.length;
}

export const HomeLayoutAdminItemSchema = z.object({
  blocks: z.array(HomeBlockSchema),
  updatedAt: z.string().nullable(),
  _etag: z.string().optional(),
});
export type HomeLayoutAdminItem = z.infer<typeof HomeLayoutAdminItemSchema>;

export const UpdateHomeLayoutInputSchema = z.object({
  blocks: z.array(HomeBlockSchema)
    .max(MAX_HOME_BLOCKS, { message: `A home layout can have at most ${MAX_HOME_BLOCKS} blocks` })
    .refine(hasUniqueIds, { message: 'Block ids must be unique' })
    .refine((b) => exactlyOneOfType(b, 'artists'), { message: 'Exactly one artists block is required' })
    .refine((b) => exactlyOneOfType(b, 'cities'), { message: 'Exactly one cities block is required' }),
});
export type UpdateHomeLayoutInput = z.infer<typeof UpdateHomeLayoutInputSchema>;
